import { useRef, useEffect } from "react";
import { Minus, Plus, Music } from "lucide-react";
import { transposeChord, transposeKey } from "@/lib/music";
import { AiBadge, ConfidenceBadge } from "@/components/Badges";

export default function ChordTimeline({ chordData, currentTime, onSeek, transpose, setTranspose, songKey }) {
  const scrollRef = useRef(null);
  const progression = chordData?.progression || [];
  const activeIdx = progression.findIndex((c) => currentTime >= c.start && currentTime < c.end);
  const current = activeIdx >= 0 ? progression[activeIdx] : null;
  const upcoming = activeIdx >= 0 && activeIdx < progression.length - 1 ? progression[activeIdx + 1] : null;

  useEffect(() => {
    if (activeIdx < 0 || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-chord="${activeIdx}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeIdx]);

  if (!progression.length) {
    return (
      <div className="rounded-xl border border-[#222A38] bg-[#131822] p-8 text-center text-sm text-slate-500">
        <Music className="mx-auto mb-2 text-slate-600" size={22} />
        Chord detection was not run for this song.
      </div>
    );
  }

  const displayKey = transposeKey(songKey?.value, transpose);
  // condensed unique progression for the header bar (first 8 distinct)
  const condensed = [];
  progression.forEach((c) => {
    const t = transposeChord(c.chord, transpose);
    if (condensed[condensed.length - 1] !== t) condensed.push(t);
  });

  return (
    <div className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-base font-bold text-white">Chords</h3>
          <AiBadge />
          {songKey && <ConfidenceBadge value={songKey.confidence} />}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono-sw text-xs text-slate-400">Transpose</span>
          <button data-testid="chord-transpose-down" onClick={() => setTranspose(Math.max(-12, transpose - 1))} className="rounded-lg bg-[#1A202C] p-1.5 text-slate-300 hover:text-white"><Minus size={14} /></button>
          <span data-testid="transpose-value" className="w-10 text-center font-mono-sw text-sm font-bold text-cyan-400">{transpose > 0 ? `+${transpose}` : transpose}</span>
          <button data-testid="chord-transpose-up" onClick={() => setTranspose(Math.min(12, transpose + 1))} className="rounded-lg bg-[#1A202C] p-1.5 text-slate-300 hover:text-white"><Plus size={14} /></button>
        </div>
      </div>

      {/* Current / upcoming / key */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-[#0B0E14] p-3 text-center ring-1 ring-cyan-500/30">
          <div className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">Now</div>
          <div data-testid="current-chord" className="font-display text-2xl font-extrabold text-cyan-300">{current ? transposeChord(current.chord, transpose) : "—"}</div>
        </div>
        <div className="rounded-xl bg-[#0B0E14] p-3 text-center ring-1 ring-[#222A38]">
          <div className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">Next</div>
          <div data-testid="next-chord" className="font-display text-2xl font-extrabold text-slate-300">{upcoming ? transposeChord(upcoming.chord, transpose) : "—"}</div>
        </div>
        <div className="rounded-xl bg-[#0B0E14] p-3 text-center ring-1 ring-[#222A38]">
          <div className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">Key</div>
          <div className="font-display text-lg font-extrabold text-amber-300">{displayKey || "—"}</div>
        </div>
      </div>

      {/* progression bar */}
      <div className="mb-3 flex flex-wrap items-center gap-1 font-mono-sw text-sm text-slate-400">
        {condensed.slice(0, 12).map((c, i) => (
          <span key={`cond-${i}`}>| <span className="text-slate-200">{c}</span> </span>
        ))}
        <span>|</span>
      </div>

      {/* scrollable chord cells */}
      <div ref={scrollRef} data-testid="chord-scroll" className="flex gap-2 overflow-x-auto pb-2">
        {progression.map((c, i) => (
          <button
            key={`${c.start}-${i}`}
            data-chord={i}
            data-testid={`chord-cell-${i}`}
            onClick={() => onSeek(c.start)}
            className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl border transition-all ${
              i === activeIdx
                ? "scale-105 border-cyan-400 bg-cyan-500/15 text-cyan-200"
                : "border-[#222A38] bg-[#0B0E14] text-slate-300 hover:border-[#3A4659]"
            }`}
          >
            <span className="font-display text-lg font-bold">{transposeChord(c.chord, transpose)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
